import { useState } from 'react';

import { ApiError } from '../../api/client.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { useDisableForecastPortal, useForecastPortal, useRegenerateForecastPortal } from './api.js';

/**
 * Admin-only card to manage the public forecast link. The link can be generated,
 * regenerated (mints a fresh token, invalidating the old URL), and disabled on demand.
 */
export function ForecastPortalCard(): JSX.Element {
  const settingsQuery = useForecastPortal();
  const regenerate = useRegenerateForecastPortal();
  const disable = useDisableForecastPortal();
  const [copied, setCopied] = useState(false);
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);

  const settings = settingsQuery.data;
  const active = Boolean(settings?.enabled && settings.token);
  const publicUrl = settings?.token ? `${window.location.origin}/f/${settings.token}` : null;

  const mutationError = regenerate.error ?? disable.error;
  const errorMessage = mutationError
    ? mutationError instanceof ApiError
      ? mutationError.message
      : 'Could not update the public forecast link. The server may still be updating — try again in a moment.'
    : null;

  return (
    <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-slate-800">Public forecast link</p>
            {active ? <Badge tone="green">Enabled</Badge> : <Badge tone="amber">Disabled</Badge>}
          </div>
          {active && publicUrl ? (
            <p className="mt-1 truncate font-mono text-xs text-slate-500">{publicUrl}</p>
          ) : null}
          <p className="mt-1 text-xs text-slate-400">
            Anyone with this link can view the combined forecast (no sign-in). Regenerating mints a
            new link and disables the old one.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {active && publicUrl ? (
            <Button
              variant="secondary"
              onClick={() => {
                void navigator.clipboard.writeText(publicUrl).then(() => {
                  setCopied(true);
                  window.setTimeout(() => setCopied(false), 2000);
                });
              }}
            >
              {copied ? 'Copied!' : 'Copy link'}
            </Button>
          ) : null}
          <Button
            variant={active ? 'ghost' : 'primary'}
            onClick={() => (active ? setRegenerateOpen(true) : regenerate.mutate())}
            isLoading={!active && regenerate.isPending}
          >
            {active ? 'Regenerate' : 'Generate link'}
          </Button>
          {active ? (
            <Button variant="ghost" onClick={() => setDisableOpen(true)}>
              Disable
            </Button>
          ) : null}
        </div>
      </div>

      {errorMessage ? <p className="mt-2 text-sm text-rose-600">{errorMessage}</p> : null}

      <ConfirmDialog
        isOpen={regenerateOpen}
        title="Regenerate public forecast link"
        message="This creates a new link and immediately disables the current one. Anyone using the old link will lose access."
        confirmLabel="Regenerate link"
        isConfirming={regenerate.isPending}
        onConfirm={() =>
          regenerate.mutate(undefined, { onSuccess: () => setRegenerateOpen(false) })
        }
        onCancel={() => setRegenerateOpen(false)}
      />
      <ConfirmDialog
        isOpen={disableOpen}
        title="Disable public forecast link"
        message="The link will stop working immediately. You can re-enable it later by regenerating."
        confirmLabel="Disable link"
        isConfirming={disable.isPending}
        onConfirm={() => disable.mutate(undefined, { onSuccess: () => setDisableOpen(false) })}
        onCancel={() => setDisableOpen(false)}
      />
    </div>
  );
}
